import { useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Trash2, ExternalLink, Copy, Download } from "lucide-react";
import { toast } from "sonner";
import { linkDetailOptions, useUpdateLink, useDeleteLink } from "@/features/links";
import type { UpdateLinkRequest, LinkStatus } from "@/features/links";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatDate, formatNumber } from "@/lib/utils";

export function LinkDetailPage() {
  const { shortCode } = useParams({ strict: false }) as { shortCode: string };
  const navigate = useNavigate();
  const { data: link, isLoading } = useQuery(linkDetailOptions(shortCode));
  const updateLink = useUpdateLink(shortCode);
  const deleteLink = useDeleteLink();
  const [showDelete, setShowDelete] = useState(false);
  const [editingUrl, setEditingUrl] = useState(false);
  const [newUrl, setNewUrl] = useState("");

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent dark:border-blue-400" />
      </div>
    );
  }

  if (!link) {
    return (
      <div className="py-12 text-center">
        <p className="text-gray-500 dark:text-gray-400">Link not found</p>
      </div>
    );
  }

  async function handleToggleStatus() {
    if (!link) return;
    const newStatus: LinkStatus = link.status === "Active" ? "Paused" : "Active";
    try {
      await updateLink.mutateAsync({ status: newStatus });
      toast.success(`Link ${newStatus === "Active" ? "activated" : "paused"}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update");
    }
  }

  async function handleUpdateUrl() {
    try {
      await updateLink.mutateAsync({ longUrl: newUrl } as UpdateLinkRequest);
      toast.success("Destination URL updated");
      setEditingUrl(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update");
    }
  }

  async function handleDelete() {
    try {
      await deleteLink.mutateAsync(shortCode);
      toast.success("Link deleted");
      navigate({ to: "/links" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete");
    }
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate({ to: "/links" })}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 font-mono">{link.shortCode}</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{link.shortUrl}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={link.status === "Active" ? "outline" : "default"}
            size="sm"
            onClick={handleToggleStatus}
            disabled={updateLink.isPending}
          >
            {link.status === "Active" ? "Pause" : "Activate"}
          </Button>
          <Button variant="destructive" size="sm" onClick={() => setShowDelete(true)}>
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Link Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Link Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Short URL</p>
              <div className="flex items-center gap-2">
                <p className="font-mono text-sm">{link.shortUrl}</p>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(link.shortUrl)}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Destination</p>
              {editingUrl ? (
                <div className="flex gap-2">
                  <Input value={newUrl} onChange={(e) => setNewUrl(e.target.value)} />
                  <Button size="sm" onClick={handleUpdateUrl}>Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingUrl(false)}>Cancel</Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <a href={link.longUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate max-w-md dark:text-blue-400">
                    {link.longUrl}
                    <ExternalLink className="ml-1 inline h-3 w-3" />
                  </a>
                  <Button variant="ghost" size="sm" onClick={() => { setNewUrl(link.longUrl); setEditingUrl(true); }}>
                    Edit
                  </Button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Type</p>
                <Badge variant={link.linkType === "General" ? "default" : "secondary"}>{link.linkType}</Badge>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</p>
                <Badge variant={link.status === "Active" ? "success" : link.status === "Paused" ? "warning" : "destructive"}>{link.status}</Badge>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Usage</p>
                <Badge variant={link.isSingleUse ? "warning" : "default"}>
                  {link.isSingleUse ? "Single Use" : "Reusable"}
                </Badge>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Created</p>
                <p className="text-sm">{formatDate(link.createdAt)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Expires</p>
                <p className="text-sm">{link.expiresAt ? formatDate(link.expiresAt) : "Never"}</p>
              </div>
              {link.customerId && (
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Customer ID</p>
                  <p className="text-sm font-mono">{link.customerId}</p>
                </div>
              )}
              {link.fallbackUrl && (
                <div className="col-span-2">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Fallback URL</p>
                  <a href={link.fallbackUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline dark:text-blue-400">
                    {link.fallbackUrl}
                  </a>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Statistics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg bg-blue-50 p-4 text-center dark:bg-blue-900/30">
                <p className="text-3xl font-bold text-blue-700 dark:text-blue-300">{formatNumber(link.totalClicks)}</p>
                <p className="text-sm text-blue-600 dark:text-blue-400">Total Clicks</p>
              </div>
              <div className="rounded-lg bg-green-50 p-4 text-center dark:bg-green-900/30">
                <p className="text-3xl font-bold text-green-700 dark:text-green-300">{formatNumber(link.uniqueClicks)}</p>
                <p className="text-sm text-green-600 dark:text-green-400">Unique Clicks</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate({ to: "/analytics", search: { shortCode } })}
            >
              View Full Analytics
            </Button>
          </CardContent>
        </Card>

        {/* QR Code */}
        {link.hasQrCode && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">QR Code</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center gap-4">
              <img
                src={`${new URL(link.shortUrl).origin}/qr/${link.shortCode}.png`}
                alt={`QR code for ${link.shortCode}`}
                className="h-48 w-48 rounded-lg border dark:border-gray-700"
              />
              <a
                href={`${new URL(link.shortUrl).origin}/qr/${link.shortCode}.png`}
                download={`${link.shortCode}-qr.png`}
                className="inline-flex items-center"
              >
                <Button variant="outline" size="sm">
                  <Download className="mr-2 h-4 w-4" />
                  Download PNG
                </Button>
              </a>
            </CardContent>
          </Card>
        )}

        {/* UTM Parameters */}
        {link.utmParameters && Object.keys(link.utmParameters).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">UTM Parameters</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(link.utmParameters).map(([key, value]) => (
                  <div key={key} className="flex justify-between text-sm">
                    <span className="font-medium text-gray-500 dark:text-gray-400">{key}</span>
                    <span className="text-gray-900 dark:text-gray-100">{value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Metadata */}
        {link.metadata && Object.keys(link.metadata).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Metadata</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(link.metadata).map(([key, value]) => (
                  <div key={key} className="flex justify-between text-sm">
                    <span className="font-medium text-gray-500 dark:text-gray-400">{key}</span>
                    <span className="text-gray-900 dark:text-gray-100">{value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {showDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="mx-4 w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-lg text-red-600 dark:text-red-400">Delete Link</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Are you sure you want to delete <strong className="font-mono">{link.shortCode}</strong>?
                This link has <strong>{formatNumber(link.totalClicks)}</strong> total clicks.
                This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowDelete(false)}>Cancel</Button>
                <Button variant="destructive" onClick={handleDelete} disabled={deleteLink.isPending}>
                  {deleteLink.isPending ? "Deleting..." : "Delete"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
